import type { OrganizationMemberRole } from '@/lib/services/organization-api';
import { stripOrgSlug } from '@/lib/org-path';

/**
 * Which company pages each member role can use. Mirrors the backend's role
 * defaults (OrganizationService ROLE_DEFAULT_PERMISSIONS) and JobService's
 * EDIT_ROLES, so the sidebar doesn't offer an interviewer the job editor or a
 * recruiter the billing page. The server remains the authority.
 */
export type CompanySection =
  | 'dashboard'
  | 'jobs'
  | 'candidates'
  | 'interviews'
  | 'pipeline'
  | 'assessments'
  | 'messages'
  | 'team'
  | 'analytics'
  | 'branding'
  | 'documents'
  | 'settings'
  | 'billing';

type Role = OrganizationMemberRole;

const ADMINS: Role[] = ['OWNER', 'ADMIN'];
const HIRING: Role[] = ['OWNER', 'ADMIN', 'HIRING_MANAGER', 'RECRUITER'];
/** Everyone on the company's side; MEMBER is a plain teammate with no hiring rights. */
const EVERYONE: Role[] = [...HIRING, 'INTERVIEWER', 'MEMBER', 'STAFF'];

export const COMPANY_ACCESS: Record<CompanySection, Role[]> = {
  dashboard: EVERYONE,
  jobs: HIRING,
  // CANDIDATES_VIEW: interviewers read the candidates they're interviewing.
  candidates: [...HIRING, 'INTERVIEWER'],
  interviews: [...HIRING, 'INTERVIEWER'],
  pipeline: HIRING,
  assessments: ['OWNER', 'ADMIN', 'HIRING_MANAGER'],
  messages: EVERYONE,
  // The team list is readable by everyone; inviting and role changes are admin-only on the page.
  team: EVERYONE,
  analytics: HIRING,
  branding: ADMINS,
  documents: EVERYONE,
  settings: ADMINS,
  billing: ADMINS,
};

/** With a custom role the server checks its own permission list instead of the defaults. */
const SECTION_PERMISSIONS: Partial<Record<CompanySection, string[]>> = {
  jobs: ['JOBS_CREATE', 'JOBS_EDIT', 'JOBS_PUBLISH'],
  candidates: ['CANDIDATES_VIEW'],
  interviews: ['INTERVIEWS_SCHEDULE', 'INTERVIEWS_ADD_FEEDBACK'],
  pipeline: ['CANDIDATES_MOVE_PIPELINE'],
  assessments: ['ASSESSMENTS_CREATE', 'ASSESSMENTS_VIEW_RESULTS'],
  analytics: ['ANALYTICS_VIEW_BASIC'],
  branding: ['ORG_MANAGE_SETTINGS'],
  settings: ['ORG_MANAGE_SETTINGS'],
  billing: ['ORG_MANAGE_BILLING'],
};

/** `null` role means it hasn't loaded yet: allow, so the sidebar doesn't flicker. */
export function canUseCompanySection(
  role: Role | null | undefined,
  section: CompanySection,
  customPermissions?: string[] | null
): boolean {
  if (!role) return true;
  if (role === 'OWNER' || role === 'ADMIN') return true;
  if (customPermissions) {
    const needed = SECTION_PERMISSIONS[section];
    if (needed) return needed.some((p) => customPermissions.includes(p));
  }
  return COMPANY_ACCESS[section].includes(role);
}

const SECTION_PATHS: Array<[string, CompanySection]> = [
  ['/company/dashboard', 'dashboard'],
  ['/company/jobs', 'jobs'],
  ['/company/candidates', 'candidates'],
  ['/company/interviews', 'interviews'],
  ['/company/pipeline', 'pipeline'],
  ['/company/assessments', 'assessments'],
  ['/company/messages', 'messages'],
  ['/company/organization', 'team'],
  ['/company/analytics', 'analytics'],
  ['/company/branding', 'branding'],
  ['/company/documents', 'documents'],
  ['/company/settings', 'settings'],
  ['/company/billing', 'billing'],
  ['/company/checkout', 'billing'],
];

export function companySectionForPath(fullPathname: string): CompanySection | null {
  const pathname = stripOrgSlug(fullPathname);
  let best: [string, CompanySection] | null = null;
  for (const entry of SECTION_PATHS) {
    const [prefix] = entry;
    if (
      (pathname === prefix || pathname.startsWith(`${prefix}/`)) &&
      (!best || prefix.length > best[0].length)
    ) {
      best = entry;
    }
  }
  return best?.[1] ?? null;
}
